import React, { useEffect, useCallback, useRef } from 'react';
import { useAppDispatch } from 'redux/hooks/reduxHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Button, Stack } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import { useGetProfileQuery, useUpdateProfileMutation } from 'redux/api/user.api';
import { useLoading } from 'shared/hooks/useLoading';
import { openAlertDialog, openCurrentModal } from 'redux/slices/modals/modals.slice';
import { EModalsNames } from 'shared/types/modal.interface';
import { EAvatarTypes, TAlertDialog } from 'shared/types/card.interface';
import { PROFILE_FORM_NAMES, PROFILE_FORM_LABELS } from 'shared/constants/form-constants';
import { FORMS_BUTTON } from 'shared/constants/buttons-constants';
import { ChangePasswordModal } from 'components/Modals/ChangePasswordModal/ChangePasswordModal';
import { TextFieldWrapper } from 'components/Input/TextFieldWrapper';
import { Avatar } from 'components/Avatar/Avatar';
import { MY_MENU } from 'shared/constants/menu-constants';
import { useLogoutMutation } from 'redux/api/auth.api';
import { DIALOG_SING_OUT } from 'shared/constants/modal-constants';
import { ProfileFormValidationSchema, TProfileFormType } from './validationSchema';

const fieldsArr = [
  PROFILE_FORM_NAMES.first_name,
  PROFILE_FORM_NAMES.last_name,
  PROFILE_FORM_NAMES.email,
  PROFILE_FORM_NAMES.personal_info,
];

export const Profile: React.FunctionComponent<any> = () => {
  const dispatch = useAppDispatch();
  const ref = useRef(null);
  const { data: profileData = null } = useGetProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();
  const [logout] = useLogoutMutation();
  useLoading(isLoading);

  const logoutDialog: TAlertDialog = {
    title: DIALOG_SING_OUT.title,
    text: DIALOG_SING_OUT.text,
    сonsentText: DIALOG_SING_OUT.сonsentText,
    dissentText: DIALOG_SING_OUT.dissentText,
    сonsentCallback: () => {
      logout();
    },
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    setError,
    reset,
  } = useForm<TProfileFormType>({
    defaultValues: {
      first_name: profileData?.first_name || '',
      last_name: profileData?.last_name || '',
      email: profileData?.email || '',
      groups_info: profileData?.user_groups?.map((group) => group.display_name).join('\n') || '',
      personal_info: profileData?.personal_info || '',
    },
    resolver: zodResolver(ProfileFormValidationSchema),
  });

  const catchSubmitError = (errorsObj: any) => {
    fieldsArr.forEach((field) => {
      setError(field, {
        type: 'custom',
        message: errorsObj?.[field]?.[0] || '',
      });
    });
  };

  const onSubmit: SubmitHandler<TProfileFormType> = async (formData: TProfileFormType) => {
    try {
      const newProfile = await updateProfile(formData).unwrap();
      reset({
        ...newProfile,
        groups_info: newProfile.user_groups?.map((group) => group.display_name).join('\n') || '',
      });
    } catch (err) {
      catchSubmitError(err?.data?.errors);
    }
  };

  const openChangePassword = () => {
    dispatch(openCurrentModal({ popupName: EModalsNames.ChangePasswordModal }));
  };

  const openLogoutDialog = () => {
    dispatch(openAlertDialog(logoutDialog));
  };

  const updateFormValues = useCallback(() => {
    if (profileData) {
      setValue(PROFILE_FORM_NAMES.first_name, profileData.first_name);
      setValue(PROFILE_FORM_NAMES.last_name, profileData.last_name);
      setValue(PROFILE_FORM_NAMES.email, profileData.email);
      setValue(
        PROFILE_FORM_NAMES.groups_info,
        profileData.user_groups?.map((group) => group.display_name).join('\n') || '',
      );
      setValue(PROFILE_FORM_NAMES.personal_info, profileData.personal_info);
    }
  }, [setValue, profileData]);

  useEffect(() => {
    updateFormValues();
  }, [updateFormValues]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {});
    if (ref.current) observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Stack
        ref={ref}
        columnGap="52px"
        rowGap="10px"
        alignItems={{ lg: 'start', md: 'start', xs: 'center' }}
      >
        <Avatar
          type={EAvatarTypes.Profile}
          firstName={profileData?.first_name || ''}
          lastName={profileData?.last_name || ''}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            display="flex"
            flexDirection="column"
            marginTop="30px"
            width={{ lg: '325px', md: '325px', sm: '90vw', xs: '90vw' }}
            rowGap={{ lg: '37px', md: '40px', sm: '40px', xs: '40px' }}
            mb="37px"
          >
            <TextFieldWrapper
              name={PROFILE_FORM_NAMES.first_name}
              control={control}
              label={PROFILE_FORM_LABELS.first_name}
              errorMessage={errors.first_name ? (errors.first_name?.message as string) : ''}
            />
            <TextFieldWrapper
              name={PROFILE_FORM_NAMES.last_name}
              control={control}
              label={PROFILE_FORM_LABELS.last_name}
              errorMessage={errors.last_name ? (errors.last_name?.message as string) : ''}
            />
            <TextFieldWrapper
              name={PROFILE_FORM_NAMES.email}
              control={control}
              label={PROFILE_FORM_LABELS.email}
              errorMessage={errors.email ? (errors.email?.message as string) : ''}
            />
            {profileData?.user_groups.length ? (
              <TextFieldWrapper
                name={PROFILE_FORM_NAMES.groups_info}
                control={control}
                label={PROFILE_FORM_LABELS.groups_info}
                errorMessage={errors.groups_info ? (errors.groups_info?.message as string) : ''}
                multiline
                rows={profileData?.user_groups.length}
                disabled
              />
            ) : null}
          </Stack>
          <TextFieldWrapper
            name={PROFILE_FORM_NAMES.personal_info}
            control={control}
            label={PROFILE_FORM_LABELS.personal_info}
            errorMessage={errors.personal_info ? (errors.personal_info?.message as string) : ''}
            multiline
            rows={6}
          />
          <Stack display="flex" flexDirection="column">
            <Button variant="link" onClick={openChangePassword} sx={{ mt: '12px' }} disableRipple>
              <KeyIcon
                sx={{
                  color: 'info.main',
                  mr: '5px',
                }}
              />
              {FORMS_BUTTON.profileChangePasswordModalOpenButton}
            </Button>
            <Stack marginTop={{ xs: '56px', sm: '56px' }} flexDirection="row">
              <Button
                variant="autoSize"
                type="submit"
                disabled={!isDirty}
                sx={{ width: '60%', marginRight: '10px' }}
              >
                {FORMS_BUTTON.profileSubmitButton}
              </Button>
              <Button
                onClick={openLogoutDialog}
                variant="defaultLight"
                sx={{
                  width: '40%',
                  display: { lg: 'none', md: 'none', sm: 'flex', xs: 'flex' },
                }}
              >
                {MY_MENU.SignOut}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Stack>
      <ChangePasswordModal />
    </>
  );
};

export default Profile;
